"use client";

import { Maximize2, Pause, Play, Volume2, VolumeX } from "lucide-react";
import type { ComponentRef, CSSProperties } from "react";
import { useCallback, useRef, useState } from "react";

import { MuxPlayer } from "@/components/post-rich-text/mux-player";
import { cn } from "@/lib/utils";

import { VsLightbox } from "./lightbox";

interface VsRecapVideoProps {
  /** Mux playback ID. Preferred — streamed via the Mux player. */
  playbackId?: string;
  /** Recap video source (mp4/webm). Fallback when no `playbackId` is set. */
  src?: string;
  /** Poster frame, shown before playback and as the no-video fallback. */
  poster?: string;
  className?: string;
}

/** Inline player — fill the box, cover-crop, hide all native controls. */
const INLINE_STYLE = {
  "--controls": "none",
  "--media-object-fit": "cover",
  height: "100%",
  width: "100%",
} as CSSProperties;

/** Spotlight player — full controls, contain so nothing is cropped. */
const SPOTLIGHT_STYLE = {
  "--media-object-fit": "contain",
  height: "100%",
  width: "100%",
} as CSSProperties;

const CONTROL_BTN =
  "pointer-events-auto inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition-colors hover:bg-black/75 focus:outline-none focus-visible:ring-2 focus-visible:ring-white";

/**
 * Hero recap video. With a Mux `playbackId` it behaves like the clip reels:
 * autoplays muted/looping, hover (or tap) to unmute, click to open a
 * near-fullscreen spotlight with full controls. Opening the spotlight mutes
 * the inline player so audio never doubles up.
 *
 * Falls back to a simple muted mp4, then a poster, then a "coming soon"
 * placeholder when no `playbackId` is set.
 */
export function VsRecapVideo({
  playbackId,
  src,
  poster,
  className,
}: VsRecapVideoProps) {
  if (playbackId) {
    return (
      <MuxRecap playbackId={playbackId} poster={poster} className={className} />
    );
  }
  return <FallbackRecap src={src} poster={poster} className={className} />;
}

interface MuxRecapProps {
  playbackId: string;
  poster?: string;
  className?: string;
}

function MuxRecap({ playbackId, poster, className }: MuxRecapProps) {
  const playerRef = useRef<ComponentRef<typeof MuxPlayer>>(null);
  const [muted, setMuted] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [playing, setPlaying] = useState(true);

  const togglePlay = useCallback(() => {
    const el = playerRef.current;
    if (!el) return;
    if (el.paused) el.play?.()?.catch(() => {});
    else el.pause?.();
  }, []);

  const toggleMute = useCallback(() => {
    const el = playerRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
    if (!el.muted) el.play?.()?.catch(() => {});
  }, []);

  // Opening the spotlight mutes the inline player so only the larger one has
  // audio.
  const open = useCallback(() => {
    const el = playerRef.current;
    if (el) el.muted = true;
    setMuted(true);
    setExpanded(true);
  }, []);
  const close = useCallback(() => setExpanded(false), []);

  return (
    <div
      className={cn(
        "group relative aspect-video w-full overflow-hidden rounded-2xl bg-black",
        className
      )}
    >
      <MuxPlayer
        ref={playerRef}
        playbackId={playbackId}
        title="Visionaries Summit London recap"
        autoPlay="muted"
        loop
        poster={poster}
        className="absolute inset-0 h-full w-full rounded-2xl"
        style={INLINE_STYLE}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />

      {/* Centre play/pause toggle. Always visible while paused; on md+ it
          otherwise fades in on hover / keyboard focus (always shown on touch). */}
      <div className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center">
        <button
          type="button"
          onClick={togglePlay}
          aria-label={playing ? "Pause recap" : "Play recap"}
          className={cn(
            "pointer-events-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-sm transition-opacity duration-200 hover:bg-black/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-white motion-reduce:transition-none",
            playing
              ? "opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 max-md:opacity-100"
              : "opacity-100"
          )}
        >
          {playing ? (
            <Pause size={24} aria-hidden />
          ) : (
            <Play size={24} aria-hidden />
          )}
        </button>
      </div>

      {/* Bottom controls — fullscreen (left) + unmute (right). Shown by default
          on touch; on md+ revealed on hover or keyboard focus. */}
      <div className="pointer-events-none absolute inset-0 z-[2] flex items-end justify-between p-3 opacity-100 transition-opacity duration-200 motion-reduce:transition-none md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100">
        <button
          type="button"
          onClick={open}
          aria-label="Open recap in spotlight"
          className={CONTROL_BTN}
        >
          <Maximize2 size={16} aria-hidden />
        </button>
        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? "Unmute recap" : "Mute recap"}
          className={CONTROL_BTN}
        >
          {muted ? (
            <VolumeX size={16} aria-hidden />
          ) : (
            <Volume2 size={16} aria-hidden />
          )}
        </button>
      </div>

      {/* Spotlight (focus-trapped via the shared Dialog). */}
      <VsLightbox
        open={expanded}
        onOpenChange={(o) => {
          if (!o) close();
        }}
        title="Recap viewer"
      >
        <div className="aspect-video max-h-[85vh] w-[92vw] overflow-hidden rounded-xl bg-black">
          <MuxPlayer
            playbackId={playbackId}
            title="Visionaries Summit London recap"
            autoPlay
            loop
            style={SPOTLIGHT_STYLE}
          />
        </div>
      </VsLightbox>
    </div>
  );
}

interface FallbackRecapProps {
  src?: string;
  poster?: string;
  className?: string;
}

/** Non-Mux fallback: a simple muted mp4, a poster, or a placeholder. */
function FallbackRecap({ src, poster, className }: FallbackRecapProps) {
  if (!src) {
    return (
      <div
        className={cn(
          "relative aspect-video w-full overflow-hidden rounded-2xl border border-[#313335] bg-[#171717]",
          className
        )}
      >
        {poster ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={poster}
            alt="Visionaries Summit London recap"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          // TODO(visionaries): recap video (see RECAP_VIDEO in data.ts).
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/30">
            <Play size={28} strokeWidth={1.5} aria-hidden />
            <span className="text-[13px]">Recap video coming soon</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative aspect-video w-full overflow-hidden rounded-2xl bg-black",
        className
      )}
    >
      <video
        className="absolute inset-0 h-full w-full object-cover"
        src={src}
        poster={poster}
        muted
        loop
        autoPlay
        playsInline
        preload="metadata"
      />
    </div>
  );
}
